import { BrowserRouter, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { MarketingShell } from "./marketing/MarketingShell";
import { OnboardingShell } from "./onboarding/OnboardingShell";
import { PlatformShell } from "./platform/PlatformShell";
import { TenantShell } from "./tenant/TenantShell";

type Shell = "marketing" | "platform" | "tenant";

// virtualdesk.ai is the only hostname pattern. For local dev:
//   - http://localhost:5173                  → marketing (default fallback)
//   - http://app.virtualdesk.ai:5173         → platform (add /etc/hosts entry)
//   - http://<code>.virtualdesk.ai:5173      → tenant   (add /etc/hosts entry)
function resolveShell(hostname: string): { shell: Shell; tenantCode?: string } {
  const h = hostname.toLowerCase();
  if (h === "virtualdesk.ai" || h === "www.virtualdesk.ai") {
    return { shell: "marketing" };
  }
  if (h === "app.virtualdesk.ai") return { shell: "platform" };
  const tenantMatch = h.match(/^([a-z0-9-]+)\.virtualdesk\.ai$/);
  if (tenantMatch) return { shell: "tenant", tenantCode: tenantMatch[1] };
  return { shell: "marketing" };
}

export function AppRouter() {
  const { shell, tenantCode } = useMemo(() => resolveShell(window.location.hostname), []);

  return (
    <BrowserRouter>
      <ShellSwitcher shell={shell} tenantCode={tenantCode} />
    </BrowserRouter>
  );
}

// Marketing host uses path-based dispatch: /onboarding/* renders the wizard,
// everything else renders the marketing site. No onboarding subdomain.
function ShellSwitcher({ shell, tenantCode }: { shell: Shell; tenantCode?: string }) {
  const loc = useLocation();
  if (shell === "marketing") {
    if (loc.pathname === "/onboarding" || loc.pathname.startsWith("/onboarding/")) {
      return <OnboardingShell />;
    }
    return <MarketingShell />;
  }
  if (shell === "platform") return <PlatformShell />;
  return <TenantShell tenantCode={tenantCode!} />;
}
